import MasterModalContext from 'contexts/MasterModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { LOYALTY_TERMS_AND_CONDITIONS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { hasSeenAutoEnrolledNotice, recordAutoEnrolledNoticeSeen } from 'loyalty/storage/autoEnrolledNotice'
import { useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { selectLoggedIn } from 'selectors/accountSelectors'

const TERMS_PATH = `/${LOYALTY_TERMS_AND_CONDITIONS_PAGE}`

/**
 * Shorter than the 10s the modal prompts wait (e.g. useShowLogin), as this is only a snackbar. Still covers a
 * second modal that opens straight after the first closes.
 */
export const MODAL_OPENED_TIMEOUT_IN_SECONDS = 1

/**
 * Welcomes a customer who was given loyalty access without asking: auto-enrolled at sign-in (LOYAL-2968) or
 * by the bulk auto-enrolment (LOYAL-2893). Legal: they're told they have access, not that they joined.
 *
 * Shown once per account in each browser. Waits for any open modal to be dismissed, then a moment longer, so
 * it isn't hidden behind the mobile number prompt sign-in can open.
 *
 * Routes mounts it behind `canShowPostConsentPrompts`, which only turns true after the first commit, so
 * AppSnackbar is already listening even when the account was fetched during server rendering.
 */
function AutoEnrolledNoticeGate() {
  const intl = useIntl()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const account = useAppSelector(state => state.loyalty.account.data)
  const givenName = useAppSelector(state => state.auth.account.givenName)
  const modalState = useContext(MasterModalContext)
  const [modalRecentlyOpened, setModalRecentlyOpened] = useState<boolean>(false)

  // Prevent showing behind other modals, e.g. the mobile number prompt sign-in can open
  useEffect(() => {
    let openOtherModalTimeout: number

    if (modalState.open) {
      setModalRecentlyOpened(true)
    }

    if (!modalState.open && modalRecentlyOpened) {
      openOtherModalTimeout = window.setTimeout(() => {
        setModalRecentlyOpened(false)
      }, MODAL_OPENED_TIMEOUT_IN_SECONDS * 1000)
    }

    return () => {
      clearTimeout(openOtherModalTimeout)
    }
  }, [modalState.open, modalRecentlyOpened])

  useEffect(() => {
    if (!isLoggedIn || modalState.open || modalRecentlyOpened || account?.enrolmentSource !== 'AUTO_ENROLLED') return
    if (hasSeenAutoEnrolledNotice(account.accountNumber)) return

    recordAutoEnrolledNoticeSeen(account.accountNumber)
    const loyaltyName = getLoyaltyProgramDisplayName()
    const welcomeMessage = givenName
      ? intl.formatMessage({
          id: 'zliqLEFvlG-Q2h0sNFVp',
          defaultMessage: 'Welcome, {givenName}. You now have access to {loyaltyName}. Start earning today.',
          description: 'Snackbar message shown to a customer automatically given access to the loyalty program, with their name',
        }, { loyaltyName, givenName })
      : intl.formatMessage({
          id: '7GDx2iFFS_3oezVEA3zS',
          defaultMessage: 'Welcome. You now have access to {loyaltyName}. Start earning today.',
          description: 'Snackbar message shown to a customer automatically given access to the loyalty program',
        }, { loyaltyName })
    showSnackbar(welcomeMessage, 'success', {
      action: {
        label: intl.formatMessage({
          id: '_xKjiR0ftBx_xkP8dtsr',
          defaultMessage: 'View terms & conditions',
          description: 'Link in the welcome snackbar for a customer automatically given access to the loyalty program, opening the program terms in a new tab',
        }),
        to: TERMS_PATH,
        target: '_blank',
      },
    })
  }, [account, givenName, intl, isLoggedIn, modalRecentlyOpened, modalState.open])

  return null
}

export default AutoEnrolledNoticeGate
